<template>
  <div class="tm-checkbox">
    <div class="tm-checkbox-group">
      <div
        v-for="(row, rowIndex) in horizontalRows"
        :key="rowIndex"
        class="tm-checkbox-row"
      >
        <label
          v-for="option in row"
          :key="option.value"
          class="tm-checkbox-label"
          :style="horizontalColStyle"
        >
          <span class="tm-checkbox-input-wrap">
            <input
              type="checkbox"
              :name="name"
              :value="option.value"
              :disabled="disabled"
              :checked="isChecked(option.value)"
              @change="handleChange"
              @focus="handleFocus"
              class="tm-checkbox-input"
            />
            <span
              class="tm-checkbox-custom"
              :class="{ 'dct-fonts-checkbox': isChecked(option.value) }"
            >
            </span>
          </span>
          <div v-html="option.label" class="tm-checkbox-text"></div>
        </label>
        <!-- 补齐空格以对齐 -->
        <template v-if="row.length < col">
          <span
            v-for="n in col - row.length"
            :key="'empty-' + n"
            class="tm-checkbox-label tm-checkbox-label-empty"
            :style="horizontalColStyle"
          ></span>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "tm-checkbox",
  props: {
    name: {
      type: String,
    },
    label: {
      type: String,
      default: "",
    },
    value: {
      type: [Array, String],
      default: () => [],
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Array,
      required: true,
      default: () => [],
    },
    validation: {
      type: Array,
      default: () => [],
    },
    col: {
      type: [String, Number],
      default: "",
    },
  },
  data() {
    return {
      // 本地状态，用于跟踪内部变化
      localValue: [],
    };
  },
  computed: {
    horizontalRows() {
      // 当为水平布局时，将options分割为每行col个
      const cols = Number(this.col) || this.options.length;
      const rows = [];
      for (let i = 0; i < this.options.length; i += cols) {
        rows.push(this.options.slice(i, i + cols));
      }
      return rows;
    },
    horizontalColStyle() {
      const cols = Number(this.col) || this.options.length;
      const width = `${100 / cols}%`;
      return {
        flex: `0 0 ${width}`,
        maxWidth: width,
      };
    },
  },
  watch: {
    // 监听外部 value 变化，同步到本地状态
    value: {
      handler(newValue) {
        let data = newValue;
        // 如果 value 是字符串，尝试解析为数组
        if (typeof newValue === "string") {
          try {
            const parsed = JSON.parse(newValue);
            if (Array.isArray(parsed)) {
              data = parsed;
            } else {
              data = [];
            }
          } catch (e) {
            // 如果解析失败，使用空数组
            data = [];
          }
        } else if (!Array.isArray(newValue)) {
          // 如果不是数组也不是字符串，使用空数组
          data = [];
        }
        // 确保数组中的值都是字符串类型，以匹配 options 中的 value 类型
        if (Array.isArray(data)) {
          data = data.map((item) => String(item));
        }
        this.localValue = data;
      },
      immediate: true,
    },
  },
  methods: {
    isChecked(optionValue) {
      // 使用本地状态而不是 props
      return this.localValue.includes(optionValue);
    },
    handleChange(event) {
      const optionValue = event.target.value;
      const checked = event.target.checked;
      let newValue = [...this.localValue];

      if (checked) {
        if (!newValue.includes(optionValue)) {
          newValue.push(optionValue);
        }
      } else {
        newValue = newValue.filter((item) => item !== optionValue);
      }

      // 更新本地状态
      this.localValue = newValue;

      this.$emit("change", this.name, newValue);

      this.validation.forEach((rule) => {
        if (rule.trigger === "change") {
          this.$emit("trigger", this.name, newValue);
        }
      });
    },
    handleFocus() {
      this.$emit("focus");
    },
  },
};
</script>

<style scoped>
.tm-checkbox {
  width: 100%;
}

.tm-checkbox-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  flex: 1;
  font-weight: 500;
  color: #333;
  position: relative;
  user-select: none;
}

.tm-checkbox-label-empty {
  background: none;
  border: none;
  pointer-events: none;
  flex: 0 0 auto;
}

.tm-checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.tm-checkbox-row {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
  width: 100%;
}

.tm-checkbox-input {
  /* 隐藏原生checkbox */
  opacity: 0;
  width: 0;
  height: 0;
  position: absolute;
  left: 0;
}

.tm-checkbox-custom {
  width: 16px;
  height: 16px;
  border: 2px solid #dcdfe6;
  border-radius: 3px;
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
  transition: border-color 0.2s, background 0.2s;
  position: relative;
  box-sizing: border-box;
  color: #409eff;
}

.tm-checkbox-input-wrap {
  display: inline-flex;
  align-items: center;
}

.tm-checkbox-label .tm-checkbox-custom .tm-checkbox-icon {
  display: none;
}

.tm-checkbox-text {
  text-align: left;
  display: inline;
}

.tm-checkbox-label input[type="checkbox"]:disabled + .tm-checkbox-custom {
  background: #f5f7fa;
  border-color: #e4e7ed;
  cursor: not-allowed;
}

.tm-checkbox-label input[type="checkbox"]:disabled ~ .tm-checkbox-text {
  color: #bbb;
  cursor: not-allowed;
}
</style>
